// Pure helpers to normalize/categorize customer timeline events for display.
// No React, no Firebase — testable.
import { resolveOrderProduct } from './productName';

const cleanName = (s) => (s && !/unknown/i.test(String(s))) ? s : '';

// Filter categories.
export const TIMELINE_CATEGORIES = {
    all: 'All',
    offers: 'Offers',
    rewards: 'Rewards',
    orders: 'Orders',
    complaints: 'Complaints',
    doctor: 'Doctor',
    refund: 'Refund/Replacement',
    courier: 'Courier/Tracking',
    communication: 'Communication',
    followup: 'Follow-up',
    notes: 'Notes',
};

// type -> { category, label }. Unknown types fall back to notes/Other.
const TYPE_META = {
    profile_updated: { category: 'notes', label: 'Profile Updated' },
    profile_photo_updated: { category: 'notes', label: 'Photograph Updated' },
    address_updated: { category: 'orders', label: 'Delivery Address Updated' },
    address_removed: { category: 'orders', label: 'Delivery Address Removed' },
    reward_credit: { category: 'rewards', label: 'Reward Points Credited' },
    reward_redeem: { category: 'rewards', label: 'Reward Points Used' },
    reward_expire: { category: 'rewards', label: 'Reward Points Expired' },
    reward_restore: { category: 'rewards', label: 'Reward Points Returned' },
    reward_reverse: { category: 'rewards', label: 'Reward Points Reversed' },
    customer_created: { category: 'orders', label: 'Customer Created' },
    customer_created_from_backfill: { category: 'orders', label: 'Customer Created' },
    order_created: { category: 'orders', label: 'Order Placed' },
    order_delivered: { category: 'orders', label: 'Order Delivered' },
    order_cancelled: { category: 'orders', label: 'Order Cancelled' },
    order_rto: { category: 'courier', label: 'Order RTO' },
    payment_received: { category: 'orders', label: 'Payment Received' },
    cod_confirmed: { category: 'orders', label: 'COD Confirmed' },
    invoice_generated: { category: 'orders', label: 'Invoice Generated' },
    packed: { category: 'courier', label: 'Packed' },
    shipped: { category: 'courier', label: 'Shipped' },
    out_for_delivery: { category: 'courier', label: 'Out for Delivery' },
    delivered: { category: 'courier', label: 'Delivered' },
    courier_status_updated: { category: 'courier', label: 'Courier Update' },
    courier_assigned: { category: 'courier', label: 'Courier Assigned' },
    awb_generated: { category: 'courier', label: 'AWB Generated' },
    complaint_ticket: { category: 'complaints', label: 'Complaint' },
    complaint_created: { category: 'complaints', label: 'Complaint Created' },
    complaint_status_changed: { category: 'complaints', label: 'Complaint Update' },
    doctor_case: { category: 'doctor', label: 'Doctor Case' },
    refund: { category: 'refund', label: 'Refund' },
    refund_issued: { category: 'refund', label: 'Refund Issued' },
    replacement: { category: 'refund', label: 'Replacement' },
    replacement_created: { category: 'refund', label: 'Replacement Created' },
    reship: { category: 'refund', label: 'Reship' },
    reship_created: { category: 'refund', label: 'Reship Created' },
    return_pickup: { category: 'refund', label: 'Return Pickup' },
    complaint_sms_update: { category: 'communication', label: 'SMS Sent' },
    sms_update: { category: 'communication', label: 'SMS Sent' },
    complaint_whatsapp_update: { category: 'communication', label: 'WhatsApp Sent' },
    whatsapp_update: { category: 'communication', label: 'WhatsApp Sent' },
    call_made: { category: 'communication', label: 'Call Made' },
    campaign_sent: { category: 'communication', label: 'Campaign Sent' },
    follow_up: { category: 'followup', label: 'Follow-up' },
    follow_up_created: { category: 'followup', label: 'Follow-up Created' },
    calendar_reminder: { category: 'followup', label: 'Reminder' },
    internal_note: { category: 'notes', label: 'Internal Note' },
    remark_added: { category: 'notes', label: 'Remark' },
    manual_note: { category: 'notes', label: 'Note' },
    offer_applied: { category: 'notes', label: 'Offer Applied' },
    do_not_ship_override: { category: 'notes', label: 'Do Not Ship Override' },
    referral: { category: 'orders', label: 'Referral' },
};

const toMs = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
};

const productOf = (order) => order ? resolveOrderProduct(order, {}) : '';

// Normalize a stored timeline event into a display row. `orderMap` (orderId ->
// repaired order snapshot) lets order events show the correct amount/product even
// when the stored event captured a stale value (e.g. ₹0 from the first backfill).
export const normalizeTimelineEvent = (ev = {}, orderMap = {}) => {
    const meta = TYPE_META[ev.type] || { category: 'notes', label: ev.type || 'Activity' };
    const order = ev.orderId ? orderMap[ev.orderId] : null;
    // Prefer the snapshot amount when the event amount is missing/zero.
    const amount = (ev.amount != null && Number(ev.amount) !== 0)
        ? Number(ev.amount)
        : (order ? Number(order.amount ?? order.total ?? order.orderValue ?? 0) : (ev.amount != null ? Number(ev.amount) : null));
    const product = cleanName(ev.product) || cleanName(Array.isArray(ev.productNames) ? ev.productNames.join(', ') : '') || productOf(order);
    const status = ev.status || (order ? order.status : '') || '';

    const description = ev.message || ev.note ||
        [product || ev.issueType, amount != null ? `₹${amount}` : null, status].filter(Boolean).join(' · ') || '';
    return {
        category: meta.category,
        label: meta.label,
        title: ev.ticketNumber ? `${meta.label} (${ev.ticketNumber})` : meta.label,
        description,
        orderId: ev.orderId || '',
        ticketNumber: ev.ticketNumber || '',
        product,
        status,
        channel: ev.channel || '',
        createdBy: ev.createdBy || ev.source || '',
        ts: toMs(ev.createdAt),
        rawType: ev.type || '',
    };
};

// Build display rows from a stored timeline. `orders` (array or object of nested
// order snapshots) is used to enrich order events with correct amount/product.
export const buildTimelineRows = (timeline, orders) => {
    const list = Array.isArray(timeline) ? timeline : Object.values(timeline || {});
    const orderList = Array.isArray(orders) ? orders : Object.values(orders || {});
    const orderMap = {};
    orderList.forEach(o => { const id = o.orderId || o.id; if (id) orderMap[id] = o; });
    return list.map(ev => normalizeTimelineEvent(ev, orderMap)).sort((a, b) => b.ts - a.ts);
};

export const hasStoredTimeline = (timeline) =>
    !!timeline && (Array.isArray(timeline) ? timeline.length > 0 : Object.keys(timeline).length > 0);
